import React, { useEffect, useState } from 'react'
import { Row, Form, Input, Tooltip, Typography, Col, Button } from 'antd'
import { EditOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'
// Local Files
import FiltersLayout from '../component/FiltersLayout'
import { setIsOpen } from '../helpers/actionHelpers'
import FiltersCount from '../component/FiltersCount'
import FiltersRadio from '../component/FiltersRadio'
import TagGroup from '../../../components/TagGroup'
import { AddButton } from './styled'
import { matchPath } from 'react-router'

const { Title } = Typography

const LOCATION_OPTIONS = [
  {
    label: 'Remote',
    name: 'remote',
    placeholder: 'Remote',
    disabled: ['postCode', 'location'],
    radio: true
  },
  {
    label: 'Zip / Post Code',
    name: 'postCode',
    placeholder: 'Add Postal Code',
    disabled: ['remote', 'location'],
    Icon: <InfoCircleOutlined />,
    // tooltipText: 'One zip code allowed per search',
    radio: true
  },
  {
    label: 'City, State',
    name: 'location',
    placeholder: 'Add City, State',
    disabled: ['remote', 'postCode'],
    radio: true
  },
  {
    label: 'Distance (miles): ',
    name: 'distance',
    placeholder: 'Distance (in miles)',
    Icon: <InfoCircleOutlined />,
    disabled: [],
    tooltipText: 'Default is 60 miles = 100 km',
    radio: false
  }
]

const SEARCH_BY_OPTIONS = [
  { label: 'One location', value: 'single' },
  {
    label: 'Multiple locations',
    value: 'multiple',
    icon: <InfoCircleOutlined />,
    tooltipText: 'Up to 3 locations'
  }
]

export default function Location({ setExistedFilters, existedFilters }) {
  const {
    location: { postCode, locations, distance: { mustHave: dt } = {} } = {},
    distance: { mustHave: distance } = {}
  } = existedFilters || {}
  const [values, setValues] = useState({
    distance: 60,
    postCode: '',
    remote: 'Remote',
    filters: [],
    active: 'remote',
    location: '',
    isOpen: false,
    updateIndex: null,
    searchBy: 'single'
  })
  const [isEdit, setIsEdit] = useState(false)
  const [editIndex, setEditIndex] = useState(-1)

  const isPostCode = postCode?.mustHave
  const allLocations = locations?.mustHave || []
  const simpleSearchPath = matchPath(location.pathname, {
    path: '/simplesearch',
    exact: true,
    strict: false
  })

  const isAddButtonDisable = (name) => {
    const limit = values.searchBy === 'multiple' ? 3 : 1
    if (values.active === 'remote' && values?.filters?.some((item) => item.location === 'Remote'))
      return true
    if (!values[name] && values.active === name) return true
    if (values.searchBy === 'multiple' && values.filters?.length >= limit) return true
    return false
  }

  const onRadioChange = (e) => {
    setEditIndex(-1)
    setIsEdit(false)
    setValues(() => ({
      ...values,
      active: e.target.name
    }))
  }

  const onInputChange = (key, value) => {
    setValues(() => ({
      ...values,
      [key]: value
    }))
  }

  const getPayloadValue = (val) => {
    const { active, postCode, distance, location, filters } = val || {}
    if (active === 'postCode') {
      return {
        ...existedFilters,
        postCode,
        distance,
        key: ['postCode', 'distance']
      }
    }
    if (active === 'excludeAll') return { ...existedFilters, key: [] }
    if (active === 'exclude') {
      return {
        ...existedFilters,
        locations: filters,
        key: ['locations']
      }
    }
    const locations = [
      ...filters,
      {
        location: active === 'location' ? location : '',
        distance: +distance
      }
    ]
    return { ...existedFilters, locations, key: ['locations'] }
  }

  const formatPayload = ({ key, ...res }) => {
    const payload = {}
    for (const k of key) {
      payload[k] = {
        mustHave: res[k]
      }
      delete res[k]
    }

    return {
      ...res,
      location: {
        ...payload
      },
      ...(simpleSearchPath ? {} : { key })
    }
  }

  const onAddButtonClick = async () => {
    const { active, distance, postCode, location, searchBy } = values
    setValues((prev) => {
      return {
        ...prev,
        location: '',
        postCode: '',
        distance: 60,
        filters: searchBy === 'single' ? [] : prev.filters // Clear filters only in single mode
      }
    })
    const payload = getPayloadValue({
      filters: searchBy === 'single' ? [] : allLocations, // Use empty array for single mode
      active,
      postCode,
      distance,
      location
    })
    const formattedPayload = formatPayload(payload)
    setExistedFilters(formattedPayload)
  }

  const onUpdateTag = () => {
    const { active, distance, updateIndex } = values
    const updatedVal = values[active]
    const updatedLocation = updatedVal === 'Remote' ? '' : updatedVal
    const filters = values.filters.map((t, i) => {
      if (i === updateIndex) return { location: updatedLocation, distance }
      return t
    })
    setValues((prev) => {
      return {
        ...prev,
        location: '',
        postCode: '',
        distance: 60
      }
    })
    setExistedFilters((prev) => {
      const payload = {
        ...prev,
        location: {
          ...(isPostCode
            ? {
                postCode: {
                  mustHave: updatedVal
                },
                distance: {
                  mustHave: +distance
                }
              }
            : {
                locations: {
                  mustHave: filters
                }
              })
        }
      }
      setIsEdit(false)
      setEditIndex(-1)
      return payload
    })
  }

  const onTagClick = (tag, ind) => {
    const [...parts] = tag.trim().split(' ')
    const distance = parts.pop()
    const val = parts.join(' ')
    const key = isPostCode ? 'postCode' : val === 'Remote' ? 'remote' : 'location'
    setIsEdit(true)
    setEditIndex(ind)
    setValues((prev) => ({
      ...prev,
      isOpen: true,
      active: key,
      updateIndex: ind,
      distance,
      [key]: val
    }))
  }

  const onDeleteTag = async (tags) => {
    const filters = tags.map((tag) => {
      const [...parts] = tag.trim().split(' ')
      const distance = parts.pop()
      const location = parts.join(' ')

      return { location: location === 'Remote' ? '' : location, distance }
    })
    setValues((prev) => ({
      ...prev,
      filters
    }))
    const payload = getPayloadValue({
      filters,
      active: 'exclude',
      postCode: '',
      distance: 60
    })
    const formattedPayload = formatPayload(payload)
    setExistedFilters(formattedPayload)
  }

  const clearAllFilters = async () => {
    setValues((prev) => ({
      ...prev,
      filters: []
    }))
    const payload = getPayloadValue({
      active: 'excludeAll'
    })
    const formattedPayload = formatPayload(payload)
    delete formattedPayload.location
    setExistedFilters(formattedPayload)
  }

  const tags = values?.filters?.length
    ? values?.filters.map(({ location, distance, postCode } = {}) => {
        const loc = postCode || (location === '' ? 'Remote' : location)
        return `${loc} ${distance}`
      })
    : []

  useEffect(() => {
    const data = postCode?.mustHave
      ? [{ postCode: postCode?.mustHave, distance: dt || distance }]
      : locations?.mustHave || []
    if (Array.isArray(data)) {
      setValues((prev) => ({
        ...prev,
        filters: data,
        distance: distance || 60
      }))
    }
  }, [distance, dt, locations?.mustHave, postCode?.mustHave])

  return (
    <FiltersLayout
      setIsOpen={() => setIsOpen(setValues, values)}
      isOpen={values.isOpen}
      title="LOCATION"
      // islocationToolTip={true}
      activeFilters={values?.filters?.length}
      clearAllFilters={() => clearAllFilters()}
    >
      {!values.filters.length && !values.isOpen && (
        <Title level={4} disabled style={{ fontSize: '14px', marginBottom: 0 }}>
          All Locations
        </Title>
      )}
      <Row>
        {!!values.filters?.length && (
          <Row style={{ marginTop: '16px' }}>
            {isPostCode ? (
              <>
                <Typography>
                  <Title level={5} className="filters-title">
                    Zip Code / Postcode
                  </Title>
                </Typography>
                <Col span={12} style={{ marginTop: '16px' }}>
                  <TagGroup
                    onClick={(tag, ind) => onTagClick(tag, ind)}
                    tags={tags}
                    edit={isEdit}
                    editIndex={editIndex}
                    onDelete={(tag) => onDeleteTag(tag)}
                    postCodeTag
                  />
                </Col>
              </>
            ) : (
              <TagGroup
                onClick={(tag, ind) => onTagClick(tag, ind)}
                tags={tags}
                edit={isEdit}
                editIndex={editIndex}
                onDelete={(tag) => onDeleteTag(tag)}
                postCodeTag
              />
            )}
          </Row>
        )}
      </Row>
      {values?.isOpen && (
        <Row style={{ marginTop: '10px' }}>
          <Title level={5}>Search By</Title>
          <Form className="block-level-form">
            <div style={{ display: 'flex', gap: '10px' }}>
              {SEARCH_BY_OPTIONS.map((opt) => (
                <FiltersRadio
                  key={opt.value}
                  isOpen={values.isOpen}
                  checked={values.searchBy === opt.value}
                  onChange={(e) => {
                    setValues((prev) => ({ ...prev, searchBy: opt.value, active: 'location' }))
                  }}
                  label={
                    opt?.icon ? (
                      <div style={{ textWrap: 'nowrap', marginLeft: '-6px' }}>
                        <span style={{ marginRight: '5px' }}>{opt.label}</span>
                        <Tooltip placement="topLeft" title={opt?.tooltipText}>
                          {opt?.icon}
                        </Tooltip>
                      </div>
                    ) : (
                      <div style={{ textWrap: 'nowrap', marginLeft: '-6px' }}>{opt.label}</div>
                    )
                  }
                  name="searchBy"
                />
              ))}
            </div>
          </Form>
        </Row>
      )}
      {values?.isOpen && (
        <Row className="city-postCode-container">
          {LOCATION_OPTIONS.filter((opt) => {
            if (values.searchBy === 'multiple' && ['remote', 'postCode'].includes(opt.name)) {
              return false
            }
            return true
          }).map(({ name, label, placeholder, Icon, disabled, tooltipText = '', radio } = {}) => {
            const isDisable =
              disabled.includes(values.active) || (name === 'postCode' && isPostCode && !isEdit)
            return (
              <Row className={'margin-top-row'} style={{ width: '100%' }} key={name}>
                <Form
                  className="block-level-form"
                  style={name === 'postCode' ? { marginTop: '-20px' } : {}}
                >
                  <FiltersCount ExcludeField={[]} title={label} classNames={'filters-sub-heading'}>
                    {Icon && !radio ? (
                      <div>
                        <span style={{ marginRight: '5px' }}>{label}</span>
                        <Tooltip placement="topLeft" title={tooltipText}>
                          {Icon}
                        </Tooltip>
                      </div>
                    ) : (
                      <FiltersRadio
                        isOpen={values.isOpen}
                        checked={values.active === name}
                        onChange={(e) => onRadioChange(e)}
                        // label={
                        //   Icon ? (
                        //     <div>
                        //       <span style={{ marginRight: '5px' }}>{label}</span>
                        //       <Tooltip placement="topLeft" title={tooltipText}>
                        //         {Icon}
                        //       </Tooltip>
                        //     </div>
                        //   ) : (
                        //     label
                        //   )
                        // }
                        label={label}
                        name={name}
                      />
                    )}
                  </FiltersCount>
                  {name !== 'remote' && (
                    <Form.Item>
                      <Row className="tags-label filters-tags"> </Row>
                      <Input
                        placeholder={placeholder}
                        className="single-select-input"
                        disabled={isDisable}
                        value={values[name] || ''}
                        onChange={(e) => onInputChange(name, e.target.value)}
                        onPressEnter={(e) => {
                          e.preventDefault()
                          if (!isAddButtonDisable(values.active)) {
                            onAddButtonClick()
                          }
                        }}
                      />
                      {!isDisable && (
                        <span
                          className="location-close-icon"
                          onClick={() => setValues((prev) => ({ ...prev, [name]: '' }))}
                        >
                          x
                        </span>
                      )}
                    </Form.Item>
                  )}
                </Form>
              </Row>
            )
          })}
          {isEdit ? (
            <Row justify="space-between">
              <Col span={6}>
                <Button
                  style={{ marginTop: '16px', borderStyle: 'solid' }}
                  onClick={() => {
                    setEditIndex(-1)
                    setIsEdit(false)
                    setValues((prev) => ({ ...prev, location: '', postCode: '', distance: 60 }))
                  }}
                >
                  Cancel
                </Button>
              </Col>
              <Col span={17}>
                <AddButton onClick={() => onUpdateTag()} icon={<EditOutlined />}>
                  Update
                </AddButton>
              </Col>
            </Row>
          ) : (
            <AddButton
              onClick={() => onAddButtonClick()}
              icon={<PlusOutlined />}
              disabled={isAddButtonDisable(values.active)}
            >
              Add
            </AddButton>
          )}
        </Row>
      )}
    </FiltersLayout>
  )
}

Location.propTypes = {
  setExistedFilters: PropTypes.func,
  existedFilters: PropTypes.object
}
